import { Controller } from "@hotwired/stimulus";
import { post } from "@rails/request.js";

const MIN_HEADER_IMAGE_WIDTH = 630;
const MIN_HEADER_IMAGE_HEIGHT = 315;
const MAX_FILE_SIZE = 5 * 1024 * 1024;

export default class extends Controller {
  static targets = [
    "customImageUploadField",
    "customImagePreview",
    "customImageSection",
    "defaultHeaderSection",
    "defaultHeaderFields",
    "headerBackgroundColorInput",
    "headerIconSelect",
    "headerBackgroundDiv",
    "headerTitleInput",
    "headerTitleElement",
    "headerDescriptionTextarea",
    "headerDescriptionElement",
    "titleInput",
    "contentTitleElement",
    "descriptionTextarea",
    "contentDescriptionElement",
    "buttonTextInput",
    "buttonElement",
    "radioGroup",
    "checkoutFormSelect",
    "saveButton",
  ];

  connect() {
    this.pendingCustomImageDataUrl = null;
    this.updateSaveButtonState();
  }

  confirmCloseIfDirty(event) {
    const unsaved = this.application.getControllerForElementAndIdentifier(
      this.element,
      "ui--unsaved-changes"
    );
    if (!unsaved || !unsaved.dirtyValue) return;

    const confirmed = window.confirm("You have unsaved changes. Are you sure you want to close?");
    if (confirmed) {
      unsaved.clearDirty();
    } else {
      event.preventDefault();
    }
  }

  clearDirtyOnSuccess(event) {
    if (!event.detail?.success) return;

    const unsaved = this.application.getControllerForElementAndIdentifier(
      this.element,
      "ui--unsaved-changes"
    );
    unsaved?.clearDirty();
  }

  updateSaveButtonState() {
    if (!this.hasSaveButtonTarget) return;

    const checkoutFormId = this.getCheckoutFormId();
    this.saveButtonTarget.disabled = checkoutFormId.length === 0;
  }

  getCheckoutFormId() {
    // Prefer the select wrapper target (Shadcn select stores actual value in a hidden input),
    // but fall back to querying by name in case markup changes.
    const root = this.hasCheckoutFormSelectTarget ? this.checkoutFormSelectTarget : this.element;
    const hiddenInput =
      root.querySelector(
        'input[data-select-target="hiddenInput"][name="checkout_form_assignment[checkout_form_id]"]'
      ) || root.querySelector('input[name="checkout_form_assignment[checkout_form_id]"]');

    return (hiddenInput?.value || "").trim();
  }

  updateFieldVisibility() {
    const imageDisplayType = this.getImageDisplayType();

    if (imageDisplayType === "custom") {
      this.customImageUploadFieldTarget.classList.remove("hidden");
      this.defaultHeaderFieldsTarget.classList.add("hidden");
    } else if (imageDisplayType === "none") {
      this.customImageUploadFieldTarget.classList.add("hidden");
      this.defaultHeaderFieldsTarget.classList.add("hidden");
      this.pendingCustomImageDataUrl = null;
    } else {
      this.customImageUploadFieldTarget.classList.add("hidden");
      this.defaultHeaderFieldsTarget.classList.remove("hidden");
      this.pendingCustomImageDataUrl = null;
    }
  }

  updateCustomImagePreview(event) {
    const fileInput = event.target;
    const file = fileInput.files[0];

    if (!file) return;

    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        const width = img.width;
        const height = img.height;

        if (file.size > MAX_FILE_SIZE) {
          alert("Please select an image that is less than 5MB in size.");
          fileInput.value = "";
          this.pendingCustomImageDataUrl = null;
          return;
        }

        if (width < MIN_HEADER_IMAGE_WIDTH || height < MIN_HEADER_IMAGE_HEIGHT) {
          alert(
            `Please select an image that is at least ${MIN_HEADER_IMAGE_WIDTH}px × ${MIN_HEADER_IMAGE_HEIGHT}px.`
          );
          fileInput.value = "";
          this.pendingCustomImageDataUrl = null;
          return;
        }

        // image is valid - store and apply preview
        this.pendingCustomImageDataUrl = e.target.result;
        this.applyCustomImagePreview();
      };

      img.src = e.target.result;
    };

    reader.readAsDataURL(file);
  }

  applyCustomImagePreview() {
    if (!this.pendingCustomImageDataUrl) return;
    if (!this.hasCustomImagePreviewTarget) return;

    this.customImagePreviewTarget.src = this.pendingCustomImageDataUrl;
  }

  updatePreviewDirectly(event) {
    const target = event.target;

    if (
      this.hasHeaderBackgroundColorInputTarget &&
      (target === this.headerBackgroundColorInputTarget ||
        this.headerBackgroundColorInputTarget.contains(target))
    ) {
      this.updateHeaderBackgroundColor();
    } else if (
      this.hasHeaderTitleInputTarget &&
      (target === this.headerTitleInputTarget || this.headerTitleInputTarget.contains(target))
    ) {
      this.updateHeaderTitle();
    } else if (
      this.hasHeaderDescriptionTextareaTarget &&
      (target === this.headerDescriptionTextareaTarget ||
        this.headerDescriptionTextareaTarget.contains(target))
    ) {
      this.updateHeaderDescription();
    } else if (
      this.hasTitleInputTarget &&
      (target === this.titleInputTarget || this.titleInputTarget.contains(target))
    ) {
      this.updateContentTitle();
    } else if (
      this.hasDescriptionTextareaTarget &&
      (target === this.descriptionTextareaTarget || this.descriptionTextareaTarget.contains(target))
    ) {
      this.updateContentDescription();
    } else if (
      this.hasButtonTextInputTarget &&
      (target === this.buttonTextInputTarget || this.buttonTextInputTarget.contains(target))
    ) {
      this.updateButtonText();
    } else if (target.name === "checkout_form_assignment[header_display_type]") {
      this.updateImageDisplayTypeVisibility();
    }
  }

  updateHeaderBackgroundColor() {
    const color = this.headerBackgroundColorInputTarget.value;
    if (this.hasHeaderBackgroundDivTarget) {
      this.headerBackgroundDivTarget.style.backgroundColor = color;
    }
  }

  updateHeaderTitle() {
    const title = this.headerTitleInputTarget.value;
    if (this.hasHeaderTitleElementTarget) {
      this.headerTitleElementTarget.textContent = title;
    }
  }

  updateHeaderDescription() {
    const description = this.headerDescriptionTextareaTarget.value;
    if (this.hasHeaderDescriptionElementTarget) {
      this.headerDescriptionElementTarget.textContent = description;
    }
  }

  updateContentTitle() {
    const title = this.titleInputTarget.value;
    if (this.hasContentTitleElementTarget) {
      this.contentTitleElementTarget.textContent = title;
    }
  }

  updateContentDescription() {
    const description = this.descriptionTextareaTarget.value;
    if (this.hasContentDescriptionElementTarget) {
      this.contentDescriptionElementTarget.textContent = description;
    }
  }

  updateButtonText() {
    const text = this.buttonTextInputTarget.value;
    if (this.hasButtonElementTarget) {
      this.buttonElementTarget.textContent = text;
    }
  }

  updateImageDisplayTypeVisibility() {
    const imageDisplayType = this.getImageDisplayType();

    if (imageDisplayType === "text") {
      if (this.hasCustomImageSectionTarget) {
        this.customImageSectionTarget.classList.add("hidden");
      }
      if (this.hasDefaultHeaderSectionTarget) {
        this.defaultHeaderSectionTarget.classList.remove("hidden");
      }
    } else if (imageDisplayType === "custom") {
      if (this.hasCustomImageSectionTarget) {
        this.customImageSectionTarget.classList.remove("hidden");
      }
      if (this.hasDefaultHeaderSectionTarget) {
        this.defaultHeaderSectionTarget.classList.add("hidden");
      }
      this.applyCustomImagePreview();
    } else if (imageDisplayType === "none") {
      if (this.hasCustomImageSectionTarget) {
        this.customImageSectionTarget.classList.add("hidden");
      }
      if (this.hasDefaultHeaderSectionTarget) {
        this.defaultHeaderSectionTarget.classList.add("hidden");
      }
    }

    this.updateFieldVisibility();
  }

  handleCategoryChange(event) {
    event.preventDefault();
    event.stopPropagation();

    const select = event.target;
    const newCategory = select.value;
    const form = select.closest("form");

    if (!newCategory) {
      return;
    }

    this.applyCategoryDefaults(form, newCategory);

    this.sendIconUpdate();
  }

  applyCategoryDefaults(form, category) {
    const defaults = this.getCategoryDefaults(category);
    Object.keys(defaults).forEach((fieldName) => {
      const value = defaults[fieldName];
      if (!value) return;

      let field = form.querySelector(`[name="${fieldName}"]`);

      if (!field) {
        const hiddenInput = form.querySelector(
          `input[data-select-target="hiddenInput"][name="${fieldName}"]`
        );
        if (hiddenInput) {
          field = hiddenInput;
        }
      }

      if (field) {
        if (field.type === "color") {
          field.value = value;
          const changeEvent = new Event("change", { bubbles: true, cancelable: true });
          changeEvent.preventDefault = () => {};
          field.dispatchEvent(changeEvent);
        } else if (field.tagName === "SELECT") {
          const option = Array.from(field.options).find((opt) => opt.value === value);
          if (option) {
            field.value = value;
            const changeEvent = new Event("change", { bubbles: true, cancelable: true });
            changeEvent.preventDefault = () => {};
            field.dispatchEvent(changeEvent);
          }
        } else if (field.type === "hidden" && field.dataset.selectTarget === "hiddenInput") {
          const selectComponent = field.closest('[data-controller*="select"]');
          if (selectComponent) {
            const selectController = this.application.getControllerForElementAndIdentifier(
              selectComponent,
              "select"
            );
            if (selectController) {
              selectController.setSelectedValue(value);
              return;
            }
          }

          field.value = value;
          const changeEvent = new Event("change", { bubbles: true, cancelable: true });
          changeEvent.preventDefault = () => {};
          field.dispatchEvent(changeEvent);
        } else {
          field.value = value;
          const inputEvent = new Event("input", { bubbles: true, cancelable: true });
          inputEvent.preventDefault = () => {};
          field.dispatchEvent(inputEvent);
        }
      }
    });

    if (defaults["checkout_form_assignment[header_display_type]"]) {
      const radio = form.querySelector(
        `input[name="checkout_form_assignment[header_display_type]"][value="${defaults["checkout_form_assignment[header_display_type]"]}"]`
      );
      if (radio) {
        radio.checked = true;
        const changeEvent = new Event("change", { bubbles: true, cancelable: true });
        changeEvent.preventDefault = () => {};
        radio.dispatchEvent(changeEvent);
      }
    }

    setTimeout(() => {
      this.updateImageDisplayTypeVisibility();
      this.sendIconUpdate();
    }, 100);
  }

  getCategoryDefaults(category) {
    const defaults = {
      Membership: {
        "checkout_form_assignment[header_title]": "Memberships",
        "checkout_form_assignment[header_description]":
          "Unlimited monthly washes, member perks & manage from your customer portal.",
        "checkout_form_assignment[title]": "Buy a Membership",
        "checkout_form_assignment[description]": "Prices vary per membership",
        "checkout_form_assignment[button_text]": "Buy Now",
        "checkout_form_assignment[header_icon]": "shield-user",
        "checkout_form_assignment[header_display_type]": "text",
        "checkout_form_assignment[header_background_color]": "#F1F5FD",
      },
      Retail: {
        "checkout_form_assignment[header_title]": "One-time Wash",
        "checkout_form_assignment[header_description]":
          "After purchase, you’ll receive a barcode to scan at the wash.",
        "checkout_form_assignment[title]": "Buy a Single Wash",
        "checkout_form_assignment[description]": "Prices vary per service",
        "checkout_form_assignment[button_text]": "Buy Now",
        "checkout_form_assignment[header_icon]": "droplets",
        "checkout_form_assignment[header_display_type]": "text",
        "checkout_form_assignment[header_background_color]": "#DFE8FA",
      },
      "Gift Cards": {
        "checkout_form_assignment[header_title]": "Gift Cards",
        "checkout_form_assignment[header_description]": "Buy $20, $30, $50 or $100 gift cards.",
        "checkout_form_assignment[title]": "Buy a Gift Card",
        "checkout_form_assignment[description]": "$20-$100 options",
        "checkout_form_assignment[button_text]": "Buy Now",
        "checkout_form_assignment[header_icon]": "hand-helping",
        "checkout_form_assignment[header_display_type]": "text",
        "checkout_form_assignment[header_background_color]": "#A0BDF0",
      },
    };

    return defaults[category] || {};
  }

  updatePreview(event) {
    event.preventDefault();
    event.stopPropagation();

    this.sendIconUpdate();
  }

  getImageDisplayType() {
    const checkedRadio = this.radioGroupTarget.querySelector(
      'input[name="checkout_form_assignment[header_display_type]"]:checked'
    );
    return checkedRadio ? checkedRadio.value : "text";
  }

  getIconFormData() {
    const formData = new FormData();

    if (this.hasHeaderIconSelectTarget) {
      const hiddenInput = this.headerIconSelectTarget.querySelector(
        'input[data-select-target="hiddenInput"]'
      );
      formData.append(
        "checkout_form_assignment[header_icon]",
        hiddenInput ? hiddenInput.value : ""
      );
    }

    return formData;
  }

  async sendIconUpdate() {
    const formData = this.getIconFormData();
    if (!formData) return;

    try {
      const response = await post(
        "/admin/customer_portals/checkout_form_assignments/preview_icon",
        {
          body: formData,
          responseKind: "turbo-stream",
        }
      );

      if (!response.ok) throw new Error("Icon update failed");

      await response.renderTurboStream();
    } catch (error) {
      console.error("Icon update failed:", error);
    }
  }
}
